import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, type Theme } from "@mui/material";
import { type ReactNode } from "react";

import { Text } from "./Text";

export interface SettingRowProps {
  title: string;
  action: ReactNode;
  actionWidth?: string;
  alignTop?: boolean;
  disabled?: boolean;
  fullWidthAction?: boolean;
  isResponsive?: boolean;
  subtitle?: ReactNode;
}

export function SettingRow({
  title,
  action,
  actionWidth,
  alignTop = false,
  disabled = false,
  fullWidthAction = false,
  isResponsive = false,
  subtitle,
}: SettingRowProps) {
  const rowAlignment = alignTop ? "flex-start" : "center";
  const shouldStackAction = fullWidthAction;
  const rowDirection = getSettingRowDirection({ isResponsive, shouldStackAction });
  const rowAlignItems = getSettingRowAlignItems({
    isResponsive,
    rowAlignment,
    shouldStackAction,
  });

  return (
    <Stack
      direction={rowDirection}
      alignItems={rowAlignItems}
      justifyContent="space-between"
      sx={(theme) => ({
        padding: theme.spacing(3, 0),
        opacity: disabled ? theme.opacity?.disabled : 1,
        gap: theme.spacing(3),
      })}
    >
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Text semibold variant="body2">
          {title}
        </Text>
        {isDefined(subtitle) && (
          <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {subtitle}
          </Text>
        )}
      </Stack>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexShrink: 0,
          justifyContent: shouldStackAction ? "flex-start" : "flex-end",
          maxWidth: "100%",
          paddingTop: alignTop ? 1 : 0,
          ...getSettingRowActionWidthSx({
            actionWidth,
            isResponsive,
            shouldStackAction,
            theme,
          }),
        })}
      >
        {action}
      </Box>
    </Stack>
  );
}

function getSettingRowDirection({
  isResponsive,
  shouldStackAction,
}: {
  isResponsive: boolean;
  shouldStackAction: boolean;
}) {
  if (shouldStackAction) {
    return "column" as const;
  }

  if (isResponsive) {
    return { xs: "column", md: "row" } as const;
  }

  return "row" as const;
}

function getSettingRowAlignItems({
  isResponsive,
  rowAlignment,
  shouldStackAction,
}: {
  isResponsive: boolean;
  rowAlignment: "center" | "flex-start";
  shouldStackAction: boolean;
}) {
  if (shouldStackAction) {
    return "stretch" as const;
  }

  if (isResponsive) {
    return { xs: "stretch", md: rowAlignment } as const;
  }

  return rowAlignment;
}

function getSettingRowActionWidthSx({
  actionWidth,
  isResponsive,
  shouldStackAction,
  theme,
}: {
  actionWidth: string | undefined;
  isResponsive: boolean;
  shouldStackAction: boolean;
  theme: Theme;
}) {
  if (shouldStackAction) {
    return { width: "100%" };
  }

  if (!isResponsive) {
    return {};
  }

  if (!isDefined(actionWidth)) {
    return { width: "100%" };
  }

  return {
    width: "100%",
    [theme.breakpoints.up("md")]: { width: actionWidth },
  };
}
